/**
 * web/src/pages/FixedExpenseTemplates.tsx
 * 固定費テンプレート管理ページ（管理者専用）
 * ★ 変更点: payment_account_key を Template 型に追加、モーダル・テーブルに反映
 */

import { useEffect, useState, useCallback, useMemo } from 'react'
import type { CSSProperties } from 'react'
import { api } from '../api/client'
import { ConfirmDialog, Toast } from '../components/ConfirmDialog'

// ================================================================
// 定数
// ================================================================
const CATEGORY_LABELS: Record<string, string> = {
  rent:             '家賃',
  credit_card:      'クレジットカード',
  pension:          '国民年金',
  health_insurance: '国民健康保険',
  dc_pension:       '確定拠出年金',
  nisa:             'NISA',
  school:           '学校・講座',
  tax:              '税金',
  other:            'その他',
}

const PAYMENT_METHOD_LABELS: Record<string, string> = {
  auto_debit:    '自動引落',
  bank_transfer: '銀行振込',
  credit_card:   'クレジットカード',
  cash:          '現金',
  other:         'その他',
}



const MONTH_OPTIONS = [
  [1, '1月'], [2, '2月'], [3, '3月'], [4, '4月'], [5, '5月'], [6, '6月'],
  [7, '7月'], [8, '8月'], [9, '9月'], [10, '10月'], [11, '11月'], [12, '12月'],
] as const

const ALL_MONTHS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12]
const ASAKA_KOKUHO_MONTHS = [7, 8, 9, 10, 11, 12, 1, 2]

const RECURRENCE_TYPE_LABELS: Record<string, string> = {
  annual:           '毎年繰り返す',
  specific_months:  '指定年月だけ発生する',
}

// ================================================================
// 型
// ================================================================
type Template = {
  id: string
  category: string
  description: string
  amount: number
  payment_day: number
  payment_method: string
  is_active: number
  notes: string | null
  active_months?: string | null
  payment_account_key?: string | null  // ★ 追加
  recurrence_type?: string             // ★ 追加: 'annual' | 'specific_months'
  occurrences_summary?: string | null  // ★ 追加: "YYYY-MM:amount:payment_day:id|..."
  created_at: string
  updated_at?: string | null
}

type OccurrenceRow = { target_month: string; amount: number; payment_day: number }

// ================================================================
// 非繰り返し固定費（specific_months）ヘルパー
// ================================================================
/** GET / が返す "YYYY-MM:amount:payment_day:id|..." 形式のサマリをパースする */
function parseOccurrencesSummary(summary: string | null | undefined): OccurrenceRow[] {
  if (!summary) return []
  return summary.split('|').map(part => {
    const [target_month, amount, payment_day] = part.split(':')
    return { target_month, amount: Number(amount), payment_day: Number(payment_day) }
  }).sort((a, b) => a.target_month.localeCompare(b.target_month))
}

function formatOccurrences(rows: OccurrenceRow[]): string {
  if (rows.length === 0) return '未設定'
  return `指定${rows.length}件（${rows.map(r => r.target_month).join('・')}）`
}

/** 「生成対象月」列のソート用の値。annual は選択月のうち最小の月（2桁ゼロ埋め）、
 *  specific_months は登録済み年月のうち最も早いもの（YYYY-MM）を使う。 */
function monthsSortValue(tpl: Template): string {
  if (tpl.recurrence_type === 'specific_months') {
    const rows = parseOccurrencesSummary(tpl.occurrences_summary)
    return rows.length > 0 ? rows[0].target_month : '9999-99'
  }
  const months = parseActiveMonths(tpl.active_months)
  return months.length > 0 ? String(Math.min(...months)).padStart(2, '0') : '99'
}

// ================================================================
// ソート（支払管理ページ・日報一覧と同じ実装パターンを流用）
// ================================================================
type SortState = { key: string; dir: 'asc' | 'desc' }

function SortTh({ label, colKey, sort, onSort, style }: {
  label: string; colKey: string; sort: SortState
  onSort: (key: string) => void; style?: CSSProperties
}) {
  const active = sort.key === colKey
  return (
    <th onClick={() => onSort(colKey)}
      style={{ cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', ...style }}>
      {label}
      <span style={{ marginLeft: 3, opacity: active ? 1 : 0.2, fontSize: 10 }}>
        {active && sort.dir === 'desc' ? '▼' : '▲'}
      </span>
    </th>
  )
}

type GenerateResult = {
  created: number
  skipped: number
  items: {
    description: string
    amount: number
    due_at: string
    status: 'created' | 'skipped'
    reason?: 'already_exists' | 'out_of_month'
  }[]
}

// ================================================================
// ヘルパー
// ================================================================
function fmt(n: number | null | undefined) { return (n ?? 0).toLocaleString('ja-JP') }
function currentMonth(): string { return new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 7) }

function parseActiveMonths(value: string | null | undefined): number[] {
  if (!value || value.trim() === '') return [...ALL_MONTHS]
  const nums = value.split(',').map(v => Number(v.trim())).filter(n => Number.isInteger(n) && n >= 1 && n <= 12)
  const unique = Array.from(new Set(nums))
  return unique.length > 0 ? unique : [...ALL_MONTHS]
}

function normalizeActiveMonths(months: number[]): string {
  return Array.from(new Set(months))
    .filter(n => Number.isInteger(n) && n >= 1 && n <= 12)
    .sort((a, b) => a - b)
    .join(',')
}

function sameSet(a: number[], b: number[]): boolean {
  return normalizeActiveMonths(a) === normalizeActiveMonths(b)
}

function formatActiveMonths(value: string | null | undefined): string {
  const months = parseActiveMonths(value)
  if (sameSet(months, ALL_MONTHS)) return '毎月'
  if (sameSet(months, ASAKA_KOKUHO_MONTHS)) return '7月〜2月'
  return months.sort((a, b) => a - b).map(m => `${m}月`).join('・')
}

// ================================================================
// テンプレート編集モーダル
// ================================================================
function TemplateModal({ initial, onClose, onSaved }: {
  initial: Partial<Template> | null
  onClose: () => void
  onSaved: () => void
}) {
  const isNew = !initial?.id
  const [form, setForm] = useState({
    category:            initial?.category            ?? 'other',
    description:         initial?.description         ?? '',
    amount:              initial?.amount              ?? 0,
    payment_day:         initial?.payment_day         ?? 27,
    payment_method:      initial?.payment_method      ?? 'auto_debit',
    notes:               initial?.notes               ?? '',
    active_months:       parseActiveMonths(initial?.active_months),
    payment_account_key: initial?.payment_account_key ?? 'business',  // ★ 追加
    recurrence_type:     initial?.recurrence_type      ?? 'annual',   // ★ 追加
  })
  // ★ 追加: 指定年月だけ発生する場合の年月ごとの金額・支払日
  const [occurrences, setOccurrences] = useState<OccurrenceRow[]>(
    parseOccurrencesSummary(initial?.occurrences_summary)
  )
  const [saving, setSaving] = useState(false)
  const [err,    setErr]    = useState<string | null>(null)

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }))
  const isSpecificMonths = form.recurrence_type === 'specific_months'

  const setActiveMonths = (months: number[]) => {
    set('active_months', Array.from(new Set(months)).filter(m => m >= 1 && m <= 12))
  }

  const toggleMonth = (m: number) => {
    const exists = form.active_months.includes(m)
    setActiveMonths(exists ? form.active_months.filter(x => x !== m) : [...form.active_months, m])
  }

  // ★ 追加: 発生年月の行操作
  const addOccurrenceRow = () => {
    setOccurrences(rows => [...rows, { target_month: currentMonth(), amount: 0, payment_day: 27 }])
  }
  const updateOccurrenceRow = (idx: number, patch: Partial<OccurrenceRow>) => {
    setOccurrences(rows => rows.map((r, i) => i === idx ? { ...r, ...patch } : r))
  }
  const removeOccurrenceRow = (idx: number) => {
    setOccurrences(rows => rows.filter((_, i) => i !== idx))
  }

  const handleSave = async () => {
    if (!form.description.trim()) { setErr('説明は必須です'); return }

    if (isSpecificMonths) {
      // ★ 指定年月だけ発生する場合: テンプレート側の金額・支払日・生成対象月は使わないため検証不要。
      //   代わりに発生年月ごとの行を検証する。
      if (occurrences.length === 0) { setErr('対象年月を1つ以上追加してください'); return }
      const seen = new Set<string>()
      for (const o of occurrences) {
        if (!/^\d{4}-\d{2}$/.test(o.target_month)) { setErr('対象年月の形式が不正です'); return }
        if (seen.has(o.target_month)) { setErr(`対象年月 ${o.target_month} が複数の行で重複しています`); return }
        seen.add(o.target_month)
        if (Number(o.amount) < 0) { setErr(`${o.target_month}: 金額は0以上で入力してください`); return }
        if (Number(o.payment_day) < 1 || Number(o.payment_day) > 31) { setErr(`${o.target_month}: 支払日は1〜31で入力してください`); return }
      }
    } else {
      if (Number(form.amount) < 0)                 { setErr('金額は0以上で入力してください'); return }
      if (Number(form.payment_day) < 1 || Number(form.payment_day) > 31) { setErr('支払日は1〜31で入力してください'); return }
      if (form.active_months.length === 0)         { setErr('生成対象月を1つ以上選択してください'); return }
    }

    setSaving(true); setErr(null)
    try {
      const payload = {
        category:            form.category,
        description:         form.description,
        // ★ specific_months では生成に使われないため固定のプレースホルダー値を送る（NOT NULL制約を満たすだけ）
        amount:              isSpecificMonths ? 0  : Number(form.amount),
        payment_day:         isSpecificMonths ? 27 : Number(form.payment_day),
        payment_method:      form.payment_method,
        notes:               form.notes.trim() || null,
        active_months:       isSpecificMonths ? undefined : normalizeActiveMonths(form.active_months),
        payment_account_key: form.payment_account_key,  // ★ 追加
        recurrence_type:     form.recurrence_type as 'annual' | 'specific_months',  // ★ 追加
      }
      let templateId: string
      if (isNew) {
        const res = await api.fixedExpenseTemplates.create(payload)
        templateId = res.template.id
      } else {
        await api.fixedExpenseTemplates.update(initial!.id!, payload)
        templateId = initial!.id!
      }

      // ★ 発生年月は常に同期する（specific_months → 入力内容を反映 / annual に切り替えた場合は空にして掃除する）
      await api.fixedExpenseTemplates.setOccurrences(
        templateId,
        isSpecificMonths ? occurrences.map(o => ({ target_month: o.target_month, amount: Number(o.amount), payment_day: Number(o.payment_day) })) : []
      )

      onSaved(); onClose()
    } catch (e: any) {
      setErr(e.message ?? '保存に失敗しました')
    } finally {
      setSaving(false)
    }
  }

  return (
    // ★ 入力途中の内容が消えないよう、背景クリックでは閉じない。
    //   閉じられるのは ✕ ボタン・キャンセルボタン・保存成功時のみ。
    <div className="overlay">
      <div className="modal" style={{ maxWidth: 560 }}>
        <div className="modal-header">
          <div className="modal-title">{isNew ? '固定費テンプレート 新規作成' : '固定費テンプレート 編集'}</div>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">
          {err && (
            <div style={{ color: 'var(--red)', fontSize: 12, marginBottom: 12,
              padding: '8px 12px', background: '#fde8e6', borderRadius: 6 }}>
              {err}
            </div>
          )}

          <div className="form-group">
            <label>カテゴリ *</label>
            <select value={form.category} onChange={e => set('category', e.target.value)}>
              {Object.entries(CATEGORY_LABELS).map(([v, l]) => (
                <option key={v} value={v}>{l}</option>
              ))}
            </select>
          </div>

          <div className="form-group">
            <label>説明 *</label>
            <input type="text" value={form.description}
              onChange={e => set('description', e.target.value)}
              placeholder="例: 国民健康保険税、Adobe CC" />
          </div>

          {/* ★ 追加: 繰り返し区分 */}
          <div className="form-group">
            <label>繰り返し区分</label>
            <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
              {Object.entries(RECURRENCE_TYPE_LABELS).map(([v, l]) => (
                <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
                  <input type="radio" checked={form.recurrence_type === v} onChange={() => set('recurrence_type', v)} />
                  {l}
                </label>
              ))}
            </div>
          </div>

          {!isSpecificMonths && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
              <div className="form-group" style={{ margin: 0 }}>
                <label>金額（円）*</label>
                <input type="number" value={form.amount} min={0}
                  onChange={e => set('amount', e.target.value)}
                  style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }} />
              </div>
              <div className="form-group" style={{ margin: 0 }}>
                <label>支払日（日）*</label>
                <input type="number" value={form.payment_day} min={1} max={31}
                  onChange={e => set('payment_day', e.target.value)}
                  placeholder="27"
                  style={{ textAlign: 'right', fontFamily: 'var(--font-mono)' }} />
                <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 2 }}>
                  ※ 月末日を超える場合は末日に丸めます
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginTop: 10 }}>
            <div className="form-group" style={{ margin: 0 }}>
              <label>支払方法</label>
              <select value={form.payment_method} onChange={e => set('payment_method', e.target.value)}>
                {Object.entries(PAYMENT_METHOD_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>
            {/* ★ 追加: 支払元口座 */}
            <div className="form-group" style={{ margin: 0 }}>
              <label>支払元口座</label>
              <select value={form.payment_account_key} onChange={e => set('payment_account_key', e.target.value)}>
                <option value="business">事業用口座</option>
                <option value="personal_manga">漫画家用個人口座</option>
              </select>
            </div>
          </div>

          {!isSpecificMonths ? (
            <div className="form-group">
              <label>生成対象月 *</label>
              <div style={{ display: 'flex', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
                {/* ★「毎月」は「すべて選択」と同じ結果になるため削除済み */}
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setActiveMonths(ASAKA_KOKUHO_MONTHS)}>
                  7月〜2月（国保税）
                </button>
                <span style={{ width: 1, background: 'var(--paper-3)', margin: '2px 2px' }} />
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setActiveMonths(ALL_MONTHS)}>
                  すべて選択
                </button>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setActiveMonths([])}>
                  すべて解除
                </button>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, minmax(0, 1fr))', gap: 6 }}>
                {MONTH_OPTIONS.map(([m, label]) => (
                  <label key={m} style={{
                    display: 'flex', alignItems: 'center', gap: 5,
                    padding: '6px 8px', border: '1px solid var(--paper-2)', borderRadius: 6,
                    fontSize: 12, cursor: 'pointer',
                    background: form.active_months.includes(m) ? '#eef7ef' : 'transparent',
                  }}>
                    <input type="checkbox" checked={form.active_months.includes(m)} onChange={() => toggleMonth(m)} />
                    {label}
                  </label>
                ))}
              </div>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6 }}>
                ※ 選択していない月は、月次支払予定の生成時に自動で対象外になります。
              </div>
            </div>
          ) : (
            // ★ 追加: 指定年月だけ発生する場合の発生年月・金額・支払日エディタ
            <div className="form-group">
              <label>対象年月・金額・支払日 *</label>
              <div className="table-wrap">
                <table>
                  <thead>
                    <tr><th>対象年月</th><th style={{ textAlign: 'right' }}>金額（円）</th><th>支払日</th><th></th></tr>
                  </thead>
                  <tbody>
                    {occurrences.length === 0 && (
                      <tr><td colSpan={4} className="text-muted" style={{ textAlign: 'center', padding: 16 }}>
                        対象年月がありません。「＋ 行を追加」で登録してください。
                      </td></tr>
                    )}
                    {occurrences.map((o, idx) => (
                      <tr key={idx}>
                        <td>
                          <input type="month" value={o.target_month}
                            onChange={e => updateOccurrenceRow(idx, { target_month: e.target.value })}
                            style={{ fontFamily: 'var(--font-mono)', fontSize: 13, width: 140 }} />
                        </td>
                        <td>
                          <input type="number" value={o.amount} min={0}
                            onChange={e => updateOccurrenceRow(idx, { amount: Number(e.target.value) })}
                            style={{ textAlign: 'right', fontFamily: 'var(--font-mono)', width: 100 }} />
                        </td>
                        <td>
                          <input type="number" value={o.payment_day} min={1} max={31}
                            onChange={e => updateOccurrenceRow(idx, { payment_day: Number(e.target.value) })}
                            style={{ fontFamily: 'var(--font-mono)', width: 64 }} />
                          日
                        </td>
                        <td>
                          <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--red)' }}
                            onClick={() => removeOccurrenceRow(idx)}>
                            削除
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button type="button" className="btn btn-outline btn-sm" style={{ marginTop: 8 }} onClick={addOccurrenceRow}>
                ＋ 行を追加
              </button>
              <div style={{ fontSize: 10, color: 'var(--ink-3)', marginTop: 6 }}>
                ※ 一覧に無い年月（別の年の同じ月を含む）には生成されません。
              </div>
            </div>
          )}

          <div className="form-group">
            <label>メモ（任意）</label>
            <textarea rows={2} value={form.notes}
              onChange={e => set('notes', e.target.value)}
              placeholder="補足事項など"
              style={{ resize: 'vertical' }} />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-outline" onClick={onClose}>キャンセル</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            {saving ? '保存中...' : '保存'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ================================================================
// 月次生成パネル（変更なし）
// ================================================================
function GeneratePanel({ onGenerated }: { onGenerated: () => void }) {
  const [targetMonth, setTargetMonth] = useState(currentMonth)
  const [result,      setResult]      = useState<GenerateResult | null>(null)
  const [loading,     setLoading]     = useState(false)
  const [err,         setErr]         = useState<string | null>(null)
  const [generated,   setGenerated]   = useState<Record<string, boolean>>({})

  const checkGenerated = useCallback(async (month: string) => {
    try {
      const expenses = await api.expenses.list({ month })
      const fixedSet: Record<string, boolean> = {}
      for (const e of expenses) { if (e.is_fixed) fixedSet[e.description] = true }
      setGenerated(fixedSet)
    } catch { /* ignore */ }
  }, [])

  useEffect(() => { checkGenerated(targetMonth) }, [targetMonth, checkGenerated])

  const handleGenerate = async () => {
    setLoading(true); setErr(null); setResult(null)
    try {
      const res = await api.fixedExpenseTemplates.generateMonth(targetMonth)
      setResult(res); onGenerated(); checkGenerated(targetMonth)
    } catch (e: any) {
      setErr(e.message ?? '生成に失敗しました')
    } finally { setLoading(false) }
  }

  const generatedCount = Object.keys(generated).length

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div className="card-header">
        <span className="card-title">月次支払予定の生成</span>
      </div>
      <div style={{ padding: '14px 20px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div>
            <label style={{ fontSize: 12, fontWeight: 600, color: 'var(--ink-2)', display: 'block', marginBottom: 4 }}>
              対象月
            </label>
            <input type="month" value={targetMonth}
              onChange={e => { setTargetMonth(e.target.value); setResult(null) }}
              style={{ fontFamily: 'var(--font-mono)', fontSize: 14, width: 160 }} />
          </div>
          <button className="btn btn-primary" onClick={handleGenerate} disabled={loading}>
            {loading ? '生成中...' : `${targetMonth} の支払予定を生成`}
          </button>
        </div>
        <div style={{ fontSize: 11, color: 'var(--ink-3)', marginTop: 8 }}>
          ※ 生成対象月に含まれない固定費は作成されません。
        </div>
        {generatedCount > 0 && (
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginTop: 8 }}>
            この月は固定費支払予定が既に {generatedCount} 件あります。
          </div>
        )}
        {err && (
          <div style={{ color: 'var(--red)', fontSize: 12, marginTop: 10,
            padding: '8px 12px', background: '#fde8e6', borderRadius: 6 }}>
            {err}
          </div>
        )}
        {result && (
          <div style={{ marginTop: 14 }}>
            <div style={{ display: 'flex', gap: 16, marginBottom: 10 }}>
              <span style={{ fontSize: 13 }}><span className="badge badge-green">作成 {result.created}件</span></span>
              {result.skipped > 0 && (
                <span style={{ fontSize: 13 }}><span className="badge badge-gray">スキップ {result.skipped}件</span></span>
              )}
            </div>
            <div className="table-wrap">
              <table>
                <thead>
                  <tr><th>説明</th><th style={{ textAlign: 'right' }}>金額</th><th>支払日</th><th>結果</th></tr>
                </thead>
                <tbody>
                  {result.items.map((item, i) => (
                    <tr key={i}>
                      <td>{item.description}</td>
                      <td className="mono" style={{ textAlign: 'right' }}>¥{fmt(item.amount)}</td>
                      <td className="mono">{item.due_at}</td>
                      <td>
                        {item.status === 'created'
                          ? <span className="badge badge-green">✓ 作成</span>
                          : item.reason === 'out_of_month'
                            ? <span className="badge badge-gray">対象外月</span>
                            : <span className="badge badge-gray">スキップ（生成済み）</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ================================================================
// メインページ
// ================================================================
export default function FixedExpenseTemplates() {
  const [templates,     setTemplates]     = useState<Template[]>([])
  const [loading,       setLoading]       = useState(true)
  const [showInactive,  setShowInactive]  = useState(false)
  const [showModal,     setShowModal]     = useState(false)
  const [editTarget,    setEditTarget]    = useState<Partial<Template> | null>(null)
  const [toast,         setToast]         = useState<{ ok: boolean; msg: string } | null>(null)
  const [confirmToggle, setConfirmToggle] = useState<Template | null>(null)
  // ★ 追加: 削除確認
  const [confirmDelete, setConfirmDelete] = useState<Template | null>(null)
  // ★ 追加: 一覧のソート状態（支払管理ページ・日報一覧と同じ実装パターン）
  const [sort,          setSort]          = useState<SortState>({ key: 'payment_day', dir: 'asc' })

  const showToast = (ok: boolean, msg: string) => {
    setToast({ ok, msg })
    setTimeout(() => setToast(null), 5000)
  }

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const data = await api.fixedExpenseTemplates.list(showInactive)
      setTemplates(data)
    } finally { setLoading(false) }
  }, [showInactive])

  useEffect(() => { load() }, [load])

  const handleToggleActive = async (tpl: Template) => {
    try {
      const res = await api.fixedExpenseTemplates.toggleActive(tpl.id)
      showToast(true, res.is_active ? `「${tpl.description}」を有効にしました` : `「${tpl.description}」を無効にしました`)
      load()
    } catch (e: any) {
      showToast(false, e.message ?? '更新に失敗しました')
    }
  }

  // ★ 追加: 削除（expenses は独立データなので無条件に物理削除できる。
  //   失敗時はサーバーのエラー文をそのままトーストで表示する＝handleToggleActiveと同じパターン）
  const handleDelete = async (tpl: Template) => {
    try {
      await api.fixedExpenseTemplates.delete(tpl.id)
      showToast(true, `「${tpl.description}」を削除しました`)
      load()
    } catch (e: any) {
      showToast(false, e.message ?? '削除に失敗しました')
    } finally {
      setConfirmDelete(null)
    }
  }

  const activeCount   = templates.filter(t => t.is_active).length
  const inactiveCount = templates.filter(t => !t.is_active).length
  const totalMonthly  = templates.filter(t => t.is_active).reduce((s, t) => s + t.amount, 0)

  // ★ 追加: ヘッダークリックでの昇順/降順切り替え（支払管理ページ・日報一覧と同じ実装パターン）
  const toggleSort = useCallback((key: string) => {
    setSort((prev) => prev.key === key
      ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: 'asc' })
  }, [])

  // ★ 追加: 表示専用の並び替え。DBの表示順・保存データ（templates state）自体は変更しない。
  const sortedTemplates = useMemo(() => {
    const list = [...templates]
    const { key, dir } = sort
    list.sort((a: any, b: any) => {
      let av: any
      let bv: any
      if (key === 'active_months') {
        av = monthsSortValue(a)
        bv = monthsSortValue(b)
      } else if (key === 'is_active') {
        av = a.is_active
        bv = b.is_active
      } else {
        av = a[key] ?? ''
        bv = b[key] ?? ''
      }
      if (typeof av === 'string' && typeof bv === 'string') {
        return dir === 'asc' ? av.localeCompare(bv, 'ja') : bv.localeCompare(av, 'ja')
      }
      return dir === 'asc' ? Number(av) - Number(bv) : Number(bv) - Number(av)
    })
    return list
  }, [templates, sort])

  return (
    <div>
      {toast && <Toast msg={toast.msg} ok={toast.ok} onClose={() => setToast(null)} />}

      {confirmToggle && (
        <ConfirmDialog
          title={confirmToggle.is_active ? '固定費を無効化' : '固定費を再有効化'}
          message={
            confirmToggle.is_active
              ? `「${confirmToggle.description}」を無効化します。\n月次生成の対象から外れますが、データは保持されます。`
              : `「${confirmToggle.description}」を再有効化します。\n次回の月次生成から対象になります。`
          }
          confirmLabel={confirmToggle.is_active ? '無効化する' : '有効化する'}
          confirmDanger={!!confirmToggle.is_active}
          onConfirm={async () => { await handleToggleActive(confirmToggle); setConfirmToggle(null) }}
          onClose={() => setConfirmToggle(null)}
        />
      )}

      {confirmDelete && (
        <ConfirmDialog
          title="固定費テンプレートを削除しますか？"
          message={`「${confirmDelete.description}」を削除します。\nこの操作は元に戻せません。`}
          confirmLabel="削除する"
          confirmDanger
          onConfirm={() => handleDelete(confirmDelete)}
          onClose={() => setConfirmDelete(null)}
        />
      )}

      {showModal && (
        <TemplateModal
          initial={editTarget}
          onClose={() => { setShowModal(false); setEditTarget(null) }}
          onSaved={() => { load(); showToast(true, '保存しました') }}
        />
      )}

      <div style={{ display: 'flex', gap: 12, marginBottom: 20, flexWrap: 'wrap' }}>
        <div className="metric" style={{ padding: '10px 16px', minWidth: 130 }}>
          <div className="metric-label">有効テンプレート</div>
          <div className="metric-value green">{activeCount}<span style={{ fontSize: 14, fontWeight: 400 }}>件</span></div>
        </div>
        <div className="metric" style={{ padding: '10px 16px', minWidth: 180 }}>
          <div className="metric-label">有効分の登録額合計</div>
          <div className="metric-value">¥{fmt(totalMonthly)}</div>
        </div>
        {inactiveCount > 0 && (
          <div className="metric" style={{ padding: '10px 16px', minWidth: 130, opacity: 0.6 }}>
            <div className="metric-label">無効テンプレート</div>
            <div className="metric-value">{inactiveCount}<span style={{ fontSize: 14, fontWeight: 400 }}>件</span></div>
          </div>
        )}
      </div>

      <GeneratePanel onGenerated={load} />

      <div className="card">
        <div className="card-header">
          <span className="card-title">固定費テンプレート一覧</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, cursor: 'pointer', color: 'var(--ink-3)' }}>
              <input type="checkbox" checked={showInactive} onChange={e => setShowInactive(e.target.checked)} />
              無効も表示
            </label>
            <button className="btn btn-primary btn-sm"
              onClick={() => { setEditTarget(null); setShowModal(true) }}>
              + 新規作成
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading" style={{ padding: 40 }}>LOADING...</div>
        ) : templates.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 40, color: 'var(--ink-3)', fontSize: 13 }}>
            テンプレートがありません。「新規作成」ボタンで追加してください。
          </div>
        ) : (
          <div className="table-wrap">
            <table>
              <thead>
                <tr>
                  {/* ★ 追加: 支払管理ページ・日報一覧と同じ SortTh によるソート可能ヘッダー */}
                  <SortTh label="カテゴリ"   colKey="category"            sort={sort} onSort={toggleSort} />
                  <SortTh label="説明"       colKey="description"         sort={sort} onSort={toggleSort} />
                  <SortTh label="区分"       colKey="recurrence_type"     sort={sort} onSort={toggleSort} />
                  <SortTh label="金額"       colKey="amount"              sort={sort} onSort={toggleSort} style={{ textAlign: 'right' }} />
                  <SortTh label="支払日"     colKey="payment_day"         sort={sort} onSort={toggleSort} />
                  <SortTh label="生成対象月" colKey="active_months"       sort={sort} onSort={toggleSort} />
                  <SortTh label="支払方法"   colKey="payment_method"      sort={sort} onSort={toggleSort} />
                  <SortTh label="支払元口座" colKey="payment_account_key" sort={sort} onSort={toggleSort} />
                  <SortTh label="状態"       colKey="is_active"           sort={sort} onSort={toggleSort} />
                  <th>メモ</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {sortedTemplates.map(tpl => {
                  const isSpecific = tpl.recurrence_type === 'specific_months'
                  const occRows = isSpecific ? parseOccurrencesSummary(tpl.occurrences_summary) : []
                  return (
                  <tr key={tpl.id} style={{ opacity: tpl.is_active ? 1 : 0.5 }}>
                    <td>
                      <span className="badge badge-gray" style={{ fontSize: 10 }}>
                        {CATEGORY_LABELS[tpl.category] ?? tpl.category}
                      </span>
                    </td>
                    <td style={{ fontWeight: 500 }}>{tpl.description}</td>
                    {/* ★ 区分列 */}
                    <td style={{ fontSize: 11 }}>
                      {isSpecific ? (
                        <span className="badge badge-blue" style={{ fontSize: 10 }}>指定年月</span>
                      ) : (
                        <span className="badge badge-gray" style={{ fontSize: 10 }}>毎年</span>
                      )}
                    </td>
                    <td className="mono" style={{ textAlign: 'right', fontWeight: 600 }}>
                      {isSpecific ? <span style={{ color: 'var(--ink-3)', fontWeight: 400 }}>月により異なる</span> : `¥${fmt(tpl.amount)}`}
                    </td>
                    <td className="mono">
                      {isSpecific ? <span style={{ color: 'var(--ink-3)' }}>—</span> : `${tpl.payment_day}日`}
                    </td>
                    <td style={{ fontSize: 12 }}>
                      {isSpecific ? formatOccurrences(occRows) : formatActiveMonths(tpl.active_months)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                      {PAYMENT_METHOD_LABELS[tpl.payment_method] ?? tpl.payment_method}
                    </td>
                    {/* ★ 支払元口座列 */}
                    <td style={{ fontSize: 11 }}>
                      {tpl.payment_account_key === 'personal_manga' ? (
                        <span className="badge badge-blue" style={{ fontSize: 10 }}>個人口座</span>
                      ) : (
                        <span className="badge badge-gray" style={{ fontSize: 10 }}>事業用</span>
                      )}
                    </td>
                    <td>
                      {tpl.is_active
                        ? <span className="badge badge-green">有効</span>
                        : <span className="badge badge-gray">無効</span>}
                    </td>
                    <td style={{ fontSize: 11, color: 'var(--ink-3)', maxWidth: 160,
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tpl.notes ?? '—'}
                    </td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      <button className="btn btn-sm btn-ghost" style={{ fontSize: 11 }}
                        onClick={() => { setEditTarget(tpl); setShowModal(true) }}>
                        編集
                      </button>
                      <button
                        className="btn btn-sm btn-ghost"
                        style={{ fontSize: 11, marginLeft: 4, color: tpl.is_active ? 'var(--ink-3)' : 'var(--green)' }}
                        onClick={() => setConfirmToggle(tpl)}
                      >
                        {tpl.is_active ? '無効化' : '再有効化'}
                      </button>
                      {/* ★ 追加: 削除（可否はサーバー側でチェックする） */}
                      <button
                        className="btn btn-sm btn-ghost"
                        style={{ fontSize: 11, marginLeft: 4, color: 'var(--red)' }}
                        onClick={() => setConfirmDelete(tpl)}
                      >
                        削除
                      </button>
                    </td>
                  </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        )}

        {!loading && templates.length > 0 && (
          <div style={{ padding: '8px 16px', fontSize: 11, color: 'var(--ink-3)', borderTop: '1px solid var(--paper-2)' }}>
            ※ 無効化しても既存の支払予定（expenses）は削除されません。
            ※ 支払日が月末日を超える場合、月次生成時に末日へ自動調整されます。
            ※ 生成対象月を設定すると、対象外の月には支払予定を作成しません。
          </div>
        )}
      </div>


    </div>
  )
}